import { useMemo, useState } from "react";

/**
 * The Done/Flagged/Pending/Not-sub. multi-select row filter shared by
 * ChecklistStatusLegend (renders the toggles) and ChecklistMatrixTable
 * (applies them).
 *
 * `items` is the same list passed to ChecklistMatrixTable — each item's
 * `cellsByStore` map is used to compute how many items match the current
 * filter, so the legend's "X of Y items" count and the table's row
 * filtering never drift apart. `storeIds` is the list of store ids to
 * check each item's cells against.
 */
export default function useChecklistStatusFilter(items, storeIds) {
  const [activeStatuses, setActiveStatuses] = useState(() => new Set());

  const toggleStatus = (status) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const visibleItemCount = useMemo(() => {
    if (activeStatuses.size === 0) return items.length;
    return items.filter((item) => storeIds.some((storeId) => activeStatuses.has(item.cellsByStore[storeId]?.status)))
      .length;
  }, [items, storeIds, activeStatuses]);

  return {
    activeStatuses,
    toggleStatus,
    visibleItemCount,
    totalItemCount: items.length,
  };
}
